import { useEffect, useState } from "react";
import { ApiError, type Filters } from "../lib/api.ts";
import {
  fetchB2b,
  fetchB2bDeals,
  type B2bChannelRow,
  type B2bDeal,
  type B2bDrill,
  type B2bEvent,
  type B2bListPage,
  type B2bListRow,
  type B2bMetric,
  type B2bParkRow,
  type B2bReport,
  type B2bSlice,
} from "../lib/b2bApi.ts";
import { money, num, pct, shortDate, weekdayDate } from "../lib/format.ts";
import { plural, t, type L } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";
import { Card, DrillNum, Kpi } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { DrawerActions } from "./exportButton.tsx";
import type { Drill } from "./dealList.tsx";
import { exportToExcel, fetchAllPages, moneyValue, type ExportColumn } from "../lib/tableExport.ts";

/**
 * Экран «B2B» — воронка сделок B2B (направление 11): школьные поездки,
 * корпоративные мероприятия. ТЗ easyCRM «B2B-воронка», раздел 12.
 *
 * Всё здесь — из `core.v_b2b_deal`, отдельно от праздников: ни одна цифра этого
 * экрана не входит в банкетные экраны, и наоборот (миграция 148).
 *
 * КАЖДОЕ ЧИСЛО ОТКРЫВАЕТСЯ СПИСКОМ, как на остальных экранах: плитка, подпись под
 * ней, строка воронки, ячейка таблицы. Список собирает сервер по той же записи
 * `METRIC`, по которой посчитано число (api/b2b.ts), со срезом строки таблицы
 * поверх, — поэтому число и длина списка не могут разойтись.
 *
 * Названия стадий, каналов, типов клиента и причин — как в CRM, по-английски:
 * это подписи карточки, которые GM видит в портале.
 */

const TX = {
  requests: { ru: "Обращения B2B", en: "B2B inquiries" },
  noPark: { ru: "без парка", en: "with no park" },
  invoicePaid: { ru: "Дошли до Invoice Paid", en: "Reached Invoice Paid" },
  ofRequests: { ru: "от обращений", en: "of inquiries" },
  invoiceSent: { ru: "счёт отправлен", en: "invoice sent" },
  revenue: { ru: "Выручка Deal won", en: "Deal won revenue" },
  avgCheck: { ru: "средний чек", en: "average deal" },
  reply: { ru: "Первый ответ GM, медиана", en: "GM first reply, median" },
  replyNone: { ru: "ответов нет", en: "no replies" },
  replied: { ru: "с ответом", en: "replied" },
  awaiting: { ru: "ждут ответа", en: "awaiting a reply" },
  noDialog: { ru: "без переписки", en: "no chat" },
  escalations: { ru: "эскалаций", en: "escalations" },
  prepaid: { ru: "Получено предоплат", en: "Prepayments received" },
  balance: { ru: "Остаток к оплате", en: "Balance due" },
  noAmount: { ru: "без суммы после Invoice Sent", en: "with no amount past Invoice Sent" },

  funnel: { ru: "Воронка B2B", en: "B2B pipeline" },
  funnelHint: { ru: "сколько сделок периода дошли до стадии", en: "how many deals of the period reached each stage" },
  count: { ru: "Сделок", en: "Deals" },
  share: { ru: "Доля", en: "Share" },
  nowOnStages: { ru: "Сейчас на стадиях:", en: "On the stages now:" },

  byPark: { ru: "Конверсия по паркам", en: "Conversion by park" },
  byParkHint: { ru: "обращение → Invoice Paid → Deal won", en: "inquiry → Invoice Paid → Deal won" },
  park: { ru: "Парк", en: "Park" },
  parkNotSet: { ru: "парк не указан", en: "park not set" },
  colRequests: { ru: "Обращения", en: "Inquiries" },
  colInvoicePaid: { ru: "Invoice Paid", en: "Invoice Paid" }, // i18n-ok: название стадии CRM
  colWon: { ru: "Deal won", en: "Deal won" }, // i18n-ok: название стадии CRM
  colLost: { ru: "Проиграно", en: "Lost" },
  colWonAmount: { ru: "Сумма выигранных, AED", en: "Won amount, AED" },
  colReply: { ru: "Ответ GM, медиана", en: "GM reply, median" },
  colEscalated: { ru: "Эскалации", en: "Escalations" },

  byChannel: { ru: "Обращения по каналам", en: "Inquiries by channel" },
  channel: { ru: "Канал", en: "Channel" },
  channelNotSet: { ru: "источник не указан", en: "source not set" },

  handoffs: { ru: "Передачи Suvvy", en: "Suvvy handoffs" },
  handoffsHint: { ru: "условие передачи GM, раздел 6.3 ТЗ", en: "why the deal went to the GM" },
  handoffNone: { ru: "код не записан", en: "no code recorded" },
  losses: { ru: "Причины потерь", en: "Loss reasons" },
  lossNone: { ru: "причина не указана", en: "no reason given" },
  noLosses: { ru: "проигранных сделок в периоде нет", en: "no lost deals in the period" },

  upcoming: { ru: "Предстоящие мероприятия B2B", en: "Upcoming B2B events" },
  upcomingHint: { ru: "от сегодня, без учёта периода", en: "from today, regardless of the period" },
  noUpcoming: { ru: "предстоящих мероприятий нет", en: "no upcoming events" },
  colDate: { ru: "Дата", en: "Date" },
  colTime: { ru: "Время", en: "Time" },
  colClient: { ru: "Клиент", en: "Client" },
  colEvent: { ru: "Мероприятие", en: "Event" },
  colGuests: { ru: "Дети / взрослые", en: "Kids / adults" },
  colRooms: { ru: "Залы", en: "Rooms" },
  colStage: { ru: "Стадия", en: "Stage" },
  colAmount: { ru: "Сумма, AED", en: "Amount, AED" },
  colPrepaid: { ru: "Предоплата, AED", en: "Prepaid, AED" },
  colBalance: { ru: "Остаток, AED", en: "Balance, AED" },
  colGm: { ru: "GM", en: "GM" }, // i18n-ok: должность, так её называют и в ТЗ
  colDeal: { ru: "Сделка", en: "Deal" },

  deals: { ru: "Сделки периода", en: "Deals of the period" },
  dealsHint: { ru: "созданные в периоде, новые сверху", en: "created in the period, newest first" },
  colCreated: { ru: "Создана", en: "Created" },
  colHandoff: { ru: "Suvvy", en: "Suvvy" }, // i18n-ok: имя бота
  noDeals: { ru: "обращений B2B за период нет", en: "no B2B inquiries in the period" },
  yes: { ru: "да", en: "yes" },
  openInBitrix: { ru: "открыть в Битриксе", en: "open in Bitrix" },

  minutes: { ru: "мин", en: "min" },
  hours: { ru: "ч", en: "h" },
  days: { ru: "д", en: "d" },

  /* --- панель со списком */
  basisCreated: { ru: "по дате создания сделки", en: "by deal creation date" },
  basisEvent: { ru: "по дате мероприятия", en: "by event date" },
  basisPayment: { ru: "по дате оплаты", en: "by payment date" },
  basisNow: { ru: "сейчас, без периода", en: "right now, regardless of the period" },
  basisUpcoming: { ru: "от сегодня", en: "from today" },
  totalAmount: { ru: "сумма", en: "amount" },
  totalPrepaid: { ru: "предоплата", en: "prepaid" },
  totalBalance: { ru: "остаток", en: "balance" },
  noMatch: { ru: "сделок по этому условию нет", en: "no deals match" },
  reachedStage: { ru: "дошли до стадии", en: "reached the stage" },
  onStageNow: { ru: "сейчас на стадии", en: "on the stage now" },
  handoffCode: { ru: "передача Suvvy", en: "Suvvy handoff" },
  escalatedTag: { ru: "эскалация", en: "escalated" },
  lossReason: { ru: "причина потери", en: "loss reason" },
  awaitingTitle: { ru: "Ждут первого ответа GM", en: "Awaiting the GM's first reply" },
  noDialogTitle: { ru: "Переданы GM без переписки", en: "Handed to the GM with no chat" },
  escalatedTitle: { ru: "Эскалации Стефану", en: "Escalations to Stefan" },
  repliedTitle: { ru: "С ответом GM", en: "With a GM reply" },
  invoiceSentTitle: { ru: "Дошли до Invoice Sent", en: "Reached Invoice Sent" },
  wonTitle: { ru: "Выиграны (Deal won)", en: "Won (Deal won)" },
  lostTitle: { ru: "Проиграны (Deal lost)", en: "Lost (Deal lost)" },
  noParkTitle: { ru: "Обращения без парка", en: "Inquiries with no park" },
  noAmountTitle: { ru: "Без суммы сделки после Invoice Sent", en: "No deal amount past Invoice Sent" },
  balanceTitle: { ru: "Остаток к оплате", en: "Balance due" },
  colGmReply: { ru: "Ответ GM", en: "GM reply" },
  firstPage: { ru: "в начало", en: "first" },
  previousPage: { ru: "← назад", en: "← previous" },
  nextPage: { ru: "далее →", en: "next →" },
  fromBooking: { ru: "из брони", en: "from the booking" },
  fromCrm: { ru: "из поля CRM", en: "from the CRM field" },
  handedAt: { ru: "передана GM", en: "handed to GM" },
  exHandedToGm: { ru: "Передана GM", en: "Handed to GM" },
  exReplyAt: { ru: "Первый ответ GM", en: "GM first reply" },
  exReplyMin: { ru: "Ответ GM, мин", en: "GM reply, min" },
  exPrepaidOn: { ru: "Дата предоплаты", en: "Prepaid on" },
  exPrepaidFrom: { ru: "Предоплата из", en: "Prepayment from" },
  exClientType: { ru: "Тип клиента", en: "Client type" },
  exEventType: { ru: "Тип мероприятия", en: "Event type" },
  exKids: { ru: "Дети", en: "Kids" },
  exAdults: { ru: "Взрослые", en: "Adults" },
  exOrganization: { ru: "Организация", en: "Organization" },
  exTitle: { ru: "Название сделки", en: "Deal title" },

  note: {
    ru: "Поток — обращения, воронка, конверсия, передачи Suvvy, причины потерь и ответ GM — считается по сделкам, созданным в периоде. Выручка — по дате мероприятия выигранных сделок, предоплаты — по дате оплаты (бронь B2B, иначе поле «Prepayment confirmed»). Ответ GM — от передачи сделки GM до первого сообщения человека клиенту, в календарных часах; эскалации — из поля сделки «There was an escalation», их считает таймер портала по рабочим часам GM. Любое число открывается списком сделок.",
    en: "Flow — inquiries, the pipeline, conversion, Suvvy handoffs, loss reasons and GM replies — counts the deals created in the period. Revenue goes by the event date of won deals, prepayments by the payment date (the B2B booking, otherwise the “Prepayment confirmed” field). GM reply runs from the hand-over to the GM to the first message a person sends the client, in calendar hours; escalations come from the deal field “There was an escalation”, set by the portal timer on GM working hours. Any figure opens the list of deals behind it.",
  },
} satisfies Record<string, L>;

/** Подписи при числе: «1 оплата», «2 оплаты», «5 оплат». */
const COUNTED = {
  requestsFoot: {
    one: { ru: "сделка создана в периоде", en: "deal created in the period" },
    few: { ru: "сделки созданы в периоде", en: "deals created in the period" },
    many: { ru: "сделок создано в периоде", en: "deals created in the period" },
  },
  revenueFoot: {
    one: { ru: "мероприятие в периоде", en: "event held in the period" },
    few: { ru: "мероприятия в периоде", en: "events held in the period" },
    many: { ru: "мероприятий в периоде", en: "events held in the period" },
  },
  prepaidFoot: {
    one: { ru: "оплата, по дате оплаты", en: "payment, by payment date" },
    few: { ru: "оплаты, по дате оплаты", en: "payments, by payment date" },
    many: { ru: "оплат, по дате оплаты", en: "payments, by payment date" },
  },
  balanceFoot: {
    one: { ru: "открытая сделка с мероприятием в периоде", en: "open deal with the event in the period" },
    few: { ru: "открытые сделки с мероприятием в периоде", en: "open deals with the event in the period" },
    many: { ru: "открытых сделок с мероприятием в периоде", en: "open deals with the event in the period" },
  },
  deals: {
    one: { ru: "сделка", en: "deal" },
    few: { ru: "сделки", en: "deals" },
    many: { ru: "сделок", en: "deals" },
  },
} satisfies Record<string, { one: L; few: L; many: L }>;

/** Условия передачи Suvvy → GM, раздел 6.3 ТЗ. */
const HANDOFF: Record<string, L> = {
  A: { ru: "A — собраны данные", en: "A — details collected" },
  B: { ru: "B — просят менеджера, цену или бронь", en: "B — asked for a manager, price or booking" },
  C: { ru: "C — вопроса нет в базе знаний", en: "C — question not in the knowledge base" },
  D: { ru: "D — клиент молчит 15 минут", en: "D — client silent for 15 minutes" },
};

/** По какой дате собран список — та же дата, что у числа над ним (api/b2b.ts). */
const BASIS: Record<B2bMetric, L> = {
  requests: TX.basisCreated,
  no_park: TX.basisCreated,
  invoice_sent: TX.basisCreated,
  invoice_paid: TX.basisCreated,
  won: TX.basisCreated,
  lost: TX.basisCreated,
  open: TX.basisCreated,
  replied: TX.basisCreated,
  awaiting_reply: TX.basisCreated,
  no_dialog: TX.basisCreated,
  escalated: TX.basisCreated,
  won_events: TX.basisEvent,
  prepayments: TX.basisPayment,
  with_balance: TX.basisEvent,
  no_amount: TX.basisNow,
  upcoming: TX.basisUpcoming,
};

const aed = (m: string | number | null | undefined): string => money(m, "AED", { withCode: false });
const aedShort = (m: string | number | null | undefined): string => money(m, "AED", { compact: true, withCode: false });

/** «3 мин», «1 ч 12 мин», «2 д 4 ч» — длительность ответа без арифметики в уме. */
function dur(sec: number | null | undefined): string {
  if (sec === null || sec === undefined) return "—";
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} ${t(TX.minutes)}`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} ${t(TX.hours)} ${m % 60} ${t(TX.minutes)}`;
  return `${Math.floor(h / 24)} ${t(TX.days)} ${h % 24} ${t(TX.hours)}`;
}

/** Голая дата разбирается как UTC и западнее Гринвича съезжает на сутки — полночь обязательна. */
const day = (iso: string | null): string => (iso ? shortDate(`${iso}T00:00:00`) : "—");

/** Детализация под числом экрана B2B. Заголовок — то, что написано рядом с числом. */
const drillOf = (title: string, slice: B2bSlice, hint?: string): Drill => ({
  kind: "b2b",
  title,
  slice,
  ...(hint !== undefined ? { hint } : {}),
});

const parkLabel = (r: { park_key: string | null; park_name: string | null }): string =>
  r.park_key ? (r.park_name ?? r.park_key) : t(TX.parkNotSet);

export function B2bScreen({ filters }: { filters: Filters }): React.ReactElement {
  const [report, setReport] = useState<B2bReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Прежний срез остаётся на экране, пока не придёт новый: смена периода не
     должна мигать пустым экраном. */
  useEffect(() => {
    let cancelled = false;
    setError(null);
    void fetchB2b(filters)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  if (error) return <div className="card err">{error}</div>;
  if (!report) return <div className="loading">{t(UI.loading)}</div>;
  return <B2bBody report={report} />;
}

function B2bBody({ report }: { report: B2bReport }): React.ReactElement {
  const s = report.totals;
  const lost = report.funnel.find((f) => f.semantics === "F");
  const steps = report.funnel.filter((f) => f.semantics !== "F");
  const scale = Math.max(1, s.requests, ...steps.map((f) => f.reached));
  const now = report.funnel.filter((f) => f.current > 0);

  return (
    <>
      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        <Kpi
          label={t(TX.requests)}
          value={num(s.requests)}
          drill={drillOf(t(TX.requests), { metric: "requests" })}
          foot={
            <>
              {plural(s.requests, COUNTED.requestsFoot)}
              {s.no_park > 0 && (
                <>
                  {" · "}
                  <DrillNum drill={drillOf(t(TX.noParkTitle), { metric: "no_park" })}>{num(s.no_park)}</DrillNum>{" "}
                  {t(TX.noPark)}
                </>
              )}
            </>
          }
        />
        <Kpi
          label={t(TX.invoicePaid)}
          value={num(s.invoice_paid)}
          drill={drillOf(t(TX.invoicePaid), { metric: "invoice_paid" })}
          foot={
            <>
              {pct(s.invoice_paid, s.requests)} {t(TX.ofRequests)} · {t(TX.invoiceSent)}{" "}
              <DrillNum drill={drillOf(t(TX.invoiceSentTitle), { metric: "invoice_sent" })}>
                {num(s.invoice_sent)}
              </DrillNum>{" "}
              · {t(TX.colWon)}{" "}
              <DrillNum drill={drillOf(t(TX.wonTitle), { metric: "won" })}>{num(s.won)}</DrillNum>
            </>
          }
        />
        <Kpi
          label={t(TX.revenue)}
          value={aedShort(s.revenue_aed_minor)}
          unit="AED"
          drill={drillOf(t(TX.revenue), { metric: "won_events" })}
          foot={
            <>
              <DrillNum drill={drillOf(t(TX.revenue), { metric: "won_events" })}>{num(s.won_events)}</DrillNum>{" "}
              {plural(s.won_events, COUNTED.revenueFoot)}
              {s.won_events > 0 &&
                ` · ${t(TX.avgCheck)} ${aedShort(Math.round(Number(s.revenue_aed_minor) / s.won_events))} AED`}
            </>
          }
        />
        <Kpi
          label={t(TX.reply)}
          value={s.reply_median_s === null ? t(TX.replyNone) : dur(s.reply_median_s)}
          drill={drillOf(t(TX.repliedTitle), { metric: "replied" })}
          foot={
            <>
              {t(TX.replied)}{" "}
              <DrillNum drill={drillOf(t(TX.repliedTitle), { metric: "replied" })}>{num(s.replied)}</DrillNum>
              {" · "}
              {t(TX.awaiting)}{" "}
              <DrillNum drill={drillOf(t(TX.awaitingTitle), { metric: "awaiting_reply" })}>
                {num(s.awaiting_reply)}
              </DrillNum>
              {" · "}
              {t(TX.noDialog)}{" "}
              <DrillNum drill={drillOf(t(TX.noDialogTitle), { metric: "no_dialog" })}>{num(s.no_dialog)}</DrillNum>
              {" · "}
              {t(TX.escalations)}{" "}
              <DrillNum drill={drillOf(t(TX.escalatedTitle), { metric: "escalated" })}>{num(s.escalated)}</DrillNum>
            </>
          }
        />
        <Kpi
          label={t(TX.prepaid)}
          value={aedShort(s.prepaid_aed_minor)}
          unit="AED"
          drill={drillOf(t(TX.prepaid), { metric: "prepayments" })}
          foot={
            <>
              <DrillNum drill={drillOf(t(TX.prepaid), { metric: "prepayments" })}>{num(s.prepayments)}</DrillNum>{" "}
              {plural(s.prepayments, COUNTED.prepaidFoot)}
            </>
          }
        />
        <Kpi
          label={t(TX.balance)}
          value={aedShort(s.balance_aed_minor)}
          unit="AED"
          drill={drillOf(t(TX.balanceTitle), { metric: "with_balance" })}
          foot={
            <>
              <DrillNum drill={drillOf(t(TX.balanceTitle), { metric: "with_balance" })}>
                {num(s.with_balance)}
              </DrillNum>{" "}
              {plural(s.with_balance, COUNTED.balanceFoot)}
              {s.no_amount > 0 && (
                <>
                  {" · "}
                  <DrillNum drill={drillOf(t(TX.noAmountTitle), { metric: "no_amount" })}>
                    {num(s.no_amount)}
                  </DrillNum>{" "}
                  {t(TX.noAmount)}
                </>
              )}
            </>
          }
        />
      </div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Card title={t(TX.funnel)} hint={t(TX.funnelHint)}>
          {s.requests === 0 ? (
            <div className="empty">{t(TX.noDeals)}</div>
          ) : (
            <>
              <div className="funnel">
                <div className="funnel-row funnel-head">
                  <span className="name" />
                  <span />
                  <span className="num">{t(TX.count)}</span>
                  <span className="num">{t(TX.share)}</span>
                </div>
                {[...steps, ...(lost ? [lost] : [])].map((f) => (
                  <div className="funnel-row" key={f.stage_id}>
                    <span className="name">{f.name}</span>
                    <div className="funnel-bar">
                      <div
                        className="funnel-fill"
                        style={{
                          width: `${(100 * f.reached) / scale}%`,
                          ...(f.semantics === "F" ? { background: "var(--bad)" } : {}),
                        }}
                      />
                    </div>
                    <span className="num">
                      <DrillNum
                        drill={drillOf(
                          `${f.name} · ${t(TX.reachedStage)}`,
                          { metric: "requests", reached: f.stage_id },
                        )}
                      >
                        {num(f.reached)}
                      </DrillNum>
                    </span>
                    <span className="num pct">{pct(f.reached, s.requests, 0)}</span>
                  </div>
                ))}
              </div>
              {now.length > 0 && (
                <div className="note" style={{ marginTop: 14 }}>
                  {t(TX.nowOnStages)}{" "}
                  {now.map((f, i) => (
                    <span key={f.stage_id}>
                      {i > 0 ? " · " : ""}
                      {f.name}{" "}
                      <DrillNum
                        drill={drillOf(`${f.name} · ${t(TX.onStageNow)}`, { metric: "requests", stage: f.stage_id })}
                      >
                        {num(f.current)}
                      </DrillNum>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
        <ParkTable rows={report.byPark} />
      </div>

      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        <B2bChannelTable rows={report.byChannel} />
        <Card title={t(TX.handoffs)} hint={t(TX.handoffsHint)}>
          <table className="data">
            <tbody>
              {report.handoffs.map((h) => {
                const label = h.code ? t(HANDOFF[h.code] ?? { ru: h.code, en: h.code }) : t(TX.handoffNone);
                return (
                  <tr key={h.code}>
                    <td>{label}</td>
                    <td className="num">
                      <DrillNum
                        drill={drillOf(`${t(TX.handoffs)} · ${label}`, { metric: "requests", handoff: h.code })}
                      >
                        {num(h.deals)}
                      </DrillNum>
                    </td>
                  </tr>
                );
              })}
              {report.handoffs.length === 0 && (
                <tr>
                  <td colSpan={2} className="empty">
                    {t(TX.noDeals)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
        <Card title={t(TX.losses)}>
          <table className="data">
            <tbody>
              {report.losses.map((l) => {
                const label = l.reason || t(TX.lossNone);
                return (
                  <tr key={l.reason}>
                    <td>{label}</td>
                    <td className="num">
                      <DrillNum drill={drillOf(`${t(TX.lostTitle)} · ${label}`, { metric: "lost", loss: l.reason })}>
                        {num(l.deals)}
                      </DrillNum>
                    </td>
                  </tr>
                );
              })}
              {report.losses.length === 0 && (
                <tr>
                  <td colSpan={2} className="empty">
                    {t(TX.noLosses)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ marginBottom: 14 }}>
        <UpcomingTable rows={report.upcoming} />
      </div>
      <DealTable rows={report.deals} />

      <div className="note" style={{ marginTop: 14 }}>
        {t(TX.note)}
      </div>
    </>
  );
}

function ParkTable({ rows }: { rows: B2bParkRow[] }): React.ReactElement {
  const sort = useSortable(rows, {
    park: { kind: "text", get: (r) => r.park_name || t(TX.parkNotSet) },
    requests: { kind: "number", get: (r) => r.requests },
    paid: { kind: "number", get: (r) => r.invoice_paid },
    won: { kind: "number", get: (r) => r.won },
    lost: { kind: "number", get: (r) => r.lost },
    amount: { kind: "number", get: (r) => Number(r.won_aed_minor) },
    reply: { kind: "number", get: (r) => r.reply_median_s },
    escalated: { kind: "number", get: (r) => r.escalated },
  });
  /* Ячейка открывает ту же величину, что плитка, со срезом этого парка. */
  const cell = (r: B2bParkRow, metric: B2bMetric, title: L, value: React.ReactNode): React.ReactElement => {
    const name = parkLabel(r);
    return (
      <DrillNum drill={drillOf(`${t(title)} · ${name}`, { metric, park: r.park_key ?? "none" })}>{value}</DrillNum>
    );
  };
  return (
    <Card title={t(TX.byPark)} hint={t(TX.byParkHint)}>
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={sort} col="park">{t(TX.park)}</Th>
              <Th sort={sort} col="requests" num>{t(TX.colRequests)}</Th>
              <Th sort={sort} col="paid" num>{t(TX.colInvoicePaid)}</Th>
              <Th sort={sort} col="won" num>{t(TX.colWon)}</Th>
              <Th sort={sort} col="lost" num>{t(TX.colLost)}</Th>
              <Th sort={sort} col="amount" num>{t(TX.colWonAmount)}</Th>
              <Th sort={sort} col="reply" num>{t(TX.colReply)}</Th>
              <Th sort={sort} col="escalated" num>{t(TX.colEscalated)}</Th>
            </tr>
          </thead>
          <tbody>
            {sort.rows.map((r) => (
              <tr key={r.park_key ?? "none"}>
                <td>{r.park_key ? r.park_name : <span className="tag tag-warn">{t(TX.parkNotSet)}</span>}</td>
                <td className="num">{cell(r, "requests", TX.requests, num(r.requests))}</td>
                <td className="num">
                  {cell(r, "invoice_paid", TX.invoicePaid, num(r.invoice_paid))}{" "}
                  <span className="muted">{pct(r.invoice_paid, r.requests, 0)}</span>
                </td>
                <td className="num">
                  {cell(r, "won", TX.wonTitle, num(r.won))} <span className="muted">{pct(r.won, r.requests, 0)}</span>
                </td>
                <td className="num">{cell(r, "lost", TX.lostTitle, num(r.lost))}</td>
                <td className="num">{cell(r, "won", TX.wonTitle, aed(r.won_aed_minor))}</td>
                <td className="num">{cell(r, "replied", TX.repliedTitle, dur(r.reply_median_s))}</td>
                <td className="num">{cell(r, "escalated", TX.escalatedTitle, num(r.escalated))}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  {t(TX.noDeals)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function B2bChannelTable({ rows }: { rows: B2bChannelRow[] }): React.ReactElement {
  const cell = (r: B2bChannelRow, metric: B2bMetric, title: L, value: number): React.ReactElement => (
    <DrillNum drill={drillOf(`${t(title)} · ${r.channel || t(TX.channelNotSet)}`, { metric, channel: r.channel })}>
      {num(value)}
    </DrillNum>
  );
  return (
    <Card title={t(TX.byChannel)}>
      <table className="data">
        <thead>
          <tr>
            <th>{t(TX.channel)}</th>
            <th className="num">{t(TX.colRequests)}</th>
            <th className="num">{t(TX.colInvoicePaid)}</th>
            <th className="num">{t(TX.colWon)}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.channel}>
              <td>{r.channel || t(TX.channelNotSet)}</td>
              <td className="num">{cell(r, "requests", TX.requests, r.requests)}</td>
              <td className="num">{cell(r, "invoice_paid", TX.invoicePaid, r.invoice_paid)}</td>
              <td className="num">{cell(r, "won", TX.wonTitle, r.won)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="empty">
                {t(TX.noDeals)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

function UpcomingTable({ rows }: { rows: B2bEvent[] }): React.ReactElement {
  return (
    <Card
      title={t(TX.upcoming)}
      hint={t(TX.upcomingHint)}
      right={
        rows.length > 0 ? (
          <DrillNum drill={drillOf(t(TX.upcoming), { metric: "upcoming" })}>
            {num(rows.length)} {plural(rows.length, COUNTED.deals)}
          </DrillNum>
        ) : undefined
      }
    >
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <th>{t(TX.colDate)}</th>
              <th>{t(TX.colTime)}</th>
              <th>{t(TX.park)}</th>
              <th>{t(TX.colClient)}</th>
              <th>{t(TX.colEvent)}</th>
              <th className="num">{t(TX.colGuests)}</th>
              <th>{t(TX.colRooms)}</th>
              <th>{t(TX.colStage)}</th>
              <th className="num">{t(TX.colAmount)}</th>
              <th className="num">{t(TX.colPrepaid)}</th>
              <th className="num">{t(TX.colBalance)}</th>
              <th>{t(TX.colGm)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.deal_id)}>
                <td>{weekdayDate(`${r.date_of_event}T00:00:00`)}</td>
                <td>{r.starts_at ? `${r.starts_at}–${r.ends_at ?? ""}` : "—"}</td>
                <td>{r.park_name ?? <span className="tag tag-warn">{t(TX.parkNotSet)}</span>}</td>
                <td>
                  <a href={r.bitrix_url} target="_blank" rel="noreferrer" title={t(TX.openInBitrix)}>
                    {r.organization || r.title || `#${r.deal_id}`}
                  </a>
                </td>
                <td>{[r.event_type, r.client_type].filter(Boolean).join(" · ") || "—"}</td>
                <td className="num">
                  {r.kids ?? "—"} / {r.adults ?? "—"}
                </td>
                <td>{r.rooms ?? "—"}</td>
                <td>{r.stage_name}</td>
                <td className="num">{r.opportunity_aed_minor ? aed(r.opportunity_aed_minor) : "—"}</td>
                <td className="num">{r.prepaid_aed_minor ? aed(r.prepaid_aed_minor) : "—"}</td>
                <td className="num">{r.balance_aed_minor ? aed(r.balance_aed_minor) : "—"}</td>
                <td>{r.manager_name ?? "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="empty">
                  {t(TX.noUpcoming)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function DealTable({ rows }: { rows: B2bDeal[] }): React.ReactElement {
  const sort = useSortable(rows, {
    deal: { kind: "number", get: (r) => Number(r.deal_id) },
    created: { kind: "text", get: (r) => r.created_day },
    client: { kind: "text", get: (r) => r.organization || r.title || "" },
    park: { kind: "text", get: (r) => r.park_name ?? "" },
    channel: { kind: "text", get: (r) => r.source_name ?? "" },
    stage: { kind: "text", get: (r) => r.stage_name ?? "" },
    event: { kind: "text", get: (r) => r.date_of_event },
    amount: { kind: "number", get: (r) => Number(r.opportunity_aed_minor ?? 0) },
    prepaid: { kind: "number", get: (r) => Number(r.prepaid_aed_minor ?? 0) },
    reply: { kind: "number", get: (r) => r.gm_reply_seconds },
  });
  return (
    <Card
      title={t(TX.deals)}
      hint={t(TX.dealsHint)}
      right={
        rows.length > 0 ? (
          <DrillNum drill={drillOf(t(TX.requests), { metric: "requests" })}>
            {num(rows.length)} {plural(rows.length, COUNTED.deals)}
          </DrillNum>
        ) : undefined
      }
    >
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={sort} col="deal">{t(TX.colDeal)}</Th>
              <Th sort={sort} col="created">{t(TX.colCreated)}</Th>
              <Th sort={sort} col="client">{t(TX.colClient)}</Th>
              <Th sort={sort} col="park">{t(TX.park)}</Th>
              <Th sort={sort} col="channel">{t(TX.channel)}</Th>
              <Th sort={sort} col="stage">{t(TX.colStage)}</Th>
              <Th sort={sort} col="event">{t(TX.colDate)}</Th>
              <Th sort={sort} col="amount" num>{t(TX.colAmount)}</Th>
              <Th sort={sort} col="prepaid" num>{t(TX.colPrepaid)}</Th>
              <Th sort={sort} col="reply" num>{t(TX.colReply)}</Th>
              <th>{t(TX.colEscalated)}</th>
              <th>{t(TX.colHandoff)}</th>
            </tr>
          </thead>
          <tbody>
            {sort.rows.map((r) => (
              <tr key={String(r.deal_id)}>
                <td>
                  <a href={r.bitrix_url} target="_blank" rel="noreferrer" title={t(TX.openInBitrix)}>
                    {String(r.deal_id)}
                  </a>
                </td>
                <td>{day(r.created_day)}</td>
                <td>{r.organization || r.title || "—"}</td>
                <td>{r.park_name ?? <span className="tag tag-warn">{t(TX.parkNotSet)}</span>}</td>
                <td>{r.source_name ?? "—"}</td>
                <td>
                  {r.stage_name}
                  {r.loss_reason ? <span className="muted"> · {r.loss_reason}</span> : null}
                </td>
                <td>{day(r.date_of_event)}</td>
                <td className="num">
                  {r.opportunity_aed_minor && Number(r.opportunity_aed_minor) > 0 ? aed(r.opportunity_aed_minor) : "—"}
                </td>
                <td className="num">{r.prepaid_aed_minor ? aed(r.prepaid_aed_minor) : "—"}</td>
                <td className="num">{dur(r.gm_reply_seconds)}</td>
                <td>{r.escalated ? <span className="tag tag-warn">{t(TX.yes)}</span> : ""}</td>
                <td>{r.suvvy_handoff_code ?? ""}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="empty">
                  {t(TX.noDeals)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------ панель со списком

/** Сколько строк за раз. Потолок сервера — 500. */
const PAGE = 200;

function listExportColumns(): Array<ExportColumn<B2bListRow>> {
  return [
    { title: t(TX.colDeal), kind: "number", width: 10, value: (r) => Number(r.deal_id) },
    { title: t(TX.colCreated), kind: "datetime", width: 17, value: (r) => r.created_local },
    { title: t(TX.exTitle), width: 30, value: (r) => r.title },
    { title: t(TX.exOrganization), width: 24, value: (r) => r.organization },
    { title: t(TX.park), width: 24, value: (r) => parkLabel(r) },
    { title: t(TX.channel), width: 16, value: (r) => r.source_name },
    { title: t(TX.exClientType), width: 14, value: (r) => r.client_type },
    { title: t(TX.exEventType), width: 16, value: (r) => r.event_type },
    { title: t(TX.colStage), width: 22, value: (r) => r.stage_name },
    { title: t(TX.lossReason), width: 20, value: (r) => r.loss_reason },
    { title: t(TX.colDate), kind: "date", width: 12, value: (r) => r.date_of_event },
    { title: t(TX.colRooms), width: 16, value: (r) => r.rooms },
    { title: t(TX.exKids), kind: "number", width: 8, value: (r) => r.kids },
    { title: t(TX.exAdults), kind: "number", width: 8, value: (r) => r.adults },
    { title: t(TX.colGm), width: 22, value: (r) => r.manager_name },
    { title: t(TX.colAmount), kind: "money", width: 13, value: (r) => moneyValue(r.opportunity_aed_minor) },
    { title: t(TX.colPrepaid), kind: "money", width: 13, value: (r) => moneyValue(r.prepaid_aed_minor) },
    { title: t(TX.exPrepaidOn), kind: "date", width: 12, value: (r) => r.prepaid_on },
    {
      title: t(TX.exPrepaidFrom),
      width: 15,
      value: (r) => (r.prepaid_source === "booking" ? t(TX.fromBooking) : r.prepaid_source === "crm" ? t(TX.fromCrm) : null),
    },
    { title: t(TX.colBalance), kind: "money", width: 13, value: (r) => moneyValue(r.balance_aed_minor) },
    { title: t(TX.exHandedToGm), kind: "datetime", width: 17, value: (r) => r.gm_in_work_local },
    { title: t(TX.exReplyAt), kind: "datetime", width: 17, value: (r) => r.gm_reply_local },
    {
      title: t(TX.exReplyMin),
      kind: "number",
      width: 12,
      value: (r) => (r.gm_reply_seconds === null ? null : Math.round(r.gm_reply_seconds / 6) / 10),
    },
    { title: t(TX.colEscalated), width: 10, value: (r) => (r.escalated ? t(TX.yes) : null) },
    { title: t(TX.colHandoff), width: 10, value: (r) => r.suvvy_handoff_code },
  ];
}

/**
 * Сделки B2B под числом экрана — та же панель, что у сделок праздников: справа во
 * весь рост, листалка по двести строк, выгрузка всего списка в Excel, переход в
 * карточку Битрикса. Сортировка — только когда весь срез уместился на странице:
 * отсортированная страница выдавала бы себя за весь список.
 */
export function B2bDealDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: B2bDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<B2bListPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = drill === null ? "" : JSON.stringify([drill.slice, filters]);

  /** Новое число — список с первой страницы: иначе откроется пустой хвост. */
  useEffect(() => {
    setOffset(0);
    setPage(null);
  }, [key]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchB2bDeals(filters, drill.slice, PAGE, offset)
      .then((p) => {
        if (alive) setPage(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  /** Escape закрывает — панель модальная, и мышь до крестика тянуть не нужно. */
  useEffect(() => {
    if (drill === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill, onClose]);

  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;
  const S = useSortable(
    rows,
    {
      deal: { kind: "text", get: (r) => r.organization || r.title || String(r.deal_id) },
      created: { kind: "text", get: (r) => r.created_local },
      park: { kind: "text", get: (r) => parkLabel(r) },
      channel: { kind: "text", get: (r) => r.source_name },
      stage: { kind: "text", get: (r) => r.stage_name },
      event: { kind: "text", get: (r) => r.date_of_event },
      amount: { kind: "number", get: (r) => (r.opportunity_aed_minor === null ? null : Number(r.opportunity_aed_minor)) },
      prepaid: { kind: "number", get: (r) => (r.prepaid_aed_minor === null ? null : Number(r.prepaid_aed_minor)) },
      balance: { kind: "number", get: (r) => (r.balance_aed_minor === null ? null : Number(r.balance_aed_minor)) },
      reply: { kind: "number", get: (r) => r.gm_reply_seconds },
      gm: { kind: "text", get: (r) => r.manager_name },
    },
    { partial: total > rows.length },
  );

  if (drill === null) return null;

  /* Весь список под числом: целиком на экране — в отсортированном порядке, иначе со всех страниц. */
  const runExport = async (progress: (done: number, total: number) => void): Promise<void> => {
    const all =
      total <= rows.length
        ? S.rows
        : await fetchAllPages((limit, off) => fetchB2bDeals(filters, drill.slice, limit, off), progress);
    await exportToExcel(drill.title, listExportColumns(), all);
  };

  /* Под заголовком — сколько сделок, по какой дате и, у денежных чисел, сумма. */
  const m = drill.slice.metric;
  const sums =
    m === "won_events"
      ? ` · ${t(TX.totalAmount)} ${aed(page?.amount_aed_minor)} AED`
      : m === "prepayments"
        ? ` · ${t(TX.totalPrepaid)} ${aed(page?.prepaid_aed_minor)} AED`
        : m === "with_balance"
          ? ` · ${t(TX.totalBalance)} ${aed(page?.balance_aed_minor)} AED`
          : "";
  const hint =
    loading && page === null
      ? t(UI.loading)
      : `${num(total)} ${plural(total, COUNTED.deals)}${sums} · ${t(BASIS[m])}` +
        (drill.hint ? ` · ${drill.hint}` : "");

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">{drill.title}</div>
            <div className="drawer-hint">{hint}</div>
          </div>
          <DrawerActions onClose={onClose} exportRun={runExport} exportDisabled={loading || total === 0} />
        </div>
        <div className="drawer-body">
          {error !== null && <div className="error">{error}</div>}
          {error === null && !loading && rows.length === 0 && <div className="empty">{t(TX.noMatch)}</div>}
          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <Th sort={S} col="deal">{t(TX.colDeal)}</Th>
                    <Th sort={S} col="created">{t(TX.colCreated)}</Th>
                    <Th sort={S} col="park">{t(TX.park)}</Th>
                    <Th sort={S} col="channel">{t(TX.channel)}</Th>
                    <Th sort={S} col="event">{t(TX.colEvent)}</Th>
                    <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
                    <Th sort={S} col="prepaid" num>{t(TX.colPrepaid)}</Th>
                    <Th sort={S} col="balance" num>{t(TX.colBalance)}</Th>
                    <Th sort={S} col="reply" num>{t(TX.colGmReply)}</Th>
                    <Th sort={S} col="gm">{t(TX.colGm)}</Th>
                    <Th sort={S} col="stage">{t(TX.colStage)}</Th>
                    {/* Кнопка перехода в Битрикс — сортировать нечего. */}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {S.rows.map((r) => (
                    <tr key={String(r.deal_id)}>
                      <td>
                        <a href={r.bitrix_url} target="_blank" rel="noreferrer">
                          {r.organization || r.title || `#${r.deal_id}`}
                        </a>
                        <div className="sub">
                          #{String(r.deal_id)}
                          {[r.client_type, r.event_type].filter(Boolean).length > 0 &&
                            ` · ${[r.client_type, r.event_type].filter(Boolean).join(" · ")}`}
                        </div>
                      </td>
                      <td className="nowrap">{r.created_local}</td>
                      <td>{r.park_key ? r.park_name : <span className="tag tag-warn">{t(TX.parkNotSet)}</span>}</td>
                      <td>{r.source_name ?? "—"}</td>
                      <td className="nowrap">
                        {day(r.date_of_event)}
                        {r.rooms !== null && <div className="sub">{r.rooms}</div>}
                        {(r.kids !== null || r.adults !== null) && (
                          <div className="sub">
                            {r.kids ?? "—"} / {r.adults ?? "—"}
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {r.opportunity_aed_minor !== null && Number(r.opportunity_aed_minor) > 0
                          ? aed(r.opportunity_aed_minor)
                          : "—"}
                      </td>
                      <td className="num">
                        {r.prepaid_aed_minor === null ? "—" : aed(r.prepaid_aed_minor)}
                        {r.prepaid_on !== null && (
                          <div className="sub">
                            {day(r.prepaid_on)} · {r.prepaid_source === "booking" ? t(TX.fromBooking) : t(TX.fromCrm)}
                          </div>
                        )}
                      </td>
                      <td className="num">{r.balance_aed_minor === null ? "—" : aed(r.balance_aed_minor)}</td>
                      <td className="num">
                        {dur(r.gm_reply_seconds)}
                        {r.gm_in_work_local !== null && (
                          <div className="sub">
                            {t(TX.handedAt)} {r.gm_in_work_local}
                          </div>
                        )}
                        {r.escalated && <span className="tag tag-warn">{t(TX.escalatedTag)}</span>}
                      </td>
                      <td>{r.manager_name ?? "—"}</td>
                      <td>
                        {r.stage_name ?? "—"}
                        {r.loss_reason !== null && <div className="sub">{r.loss_reason}</div>}
                        {r.suvvy_handoff_code !== null && (
                          <div className="sub">
                            {t(TX.handoffCode)} {r.suvvy_handoff_code}
                          </div>
                        )}
                      </td>
                      <td className="nowrap">
                        <a className="btn-ghost" href={r.bitrix_url} target="_blank" rel="noreferrer">
                          Bitrix ↗
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > rows.length && (
            <div className="drawer-foot">
              <span style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn-ghost" disabled={offset === 0 || loading} onClick={() => setOffset(0)}>
                  {t(TX.firstPage)}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t(TX.previousPage)}
                </button>
              </span>
              <span className="sub">
                {num(offset + 1)}–{num(offset + rows.length)} {t(UI.of)} {num(total)}
              </span>
              <button
                type="button"
                className="btn-ghost"
                disabled={offset + rows.length >= total || loading}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t(TX.nextPage)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
